import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { isCapacitorPlatform } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { HyperTrack } from "hypertrack-sdk-ionic-capacitor";
import { useCallback, useEffect, useRef } from "react";

export enum HyperTrackSdkState {
  NOT_INITIALIZED = "NOT_INITIALIZED",
  INITIALIZED = "INITIALIZED",
  INITIALIZATION_FAILED = "INITIALIZATION_FAILED",
  RESET_FAILED = "RESET_FAILED",
  INITIALIZING = "INITIALIZING",
  RESETTING = "RESETTING",
}

interface InitialState {
  allowMockLocation: boolean;
}

/**
 * Associates the device with the user by setting the 'name' of the device and the 'driver_handle' to the workers userId
 * @param userId - the userId of the worker
 */
async function initializeHyperTrack(params: {
  userId: string;
  allowMockLocation: boolean;
}): Promise<InitialState> {
  const { userId, allowMockLocation } = params;
  const initialState = { allowMockLocation: await HyperTrack.getAllowMockLocation() };

  await Promise.all([
    HyperTrack.setName(userId),
    HyperTrack.setMetadata({ driver_handle: userId }),
    HyperTrack.setAllowMockLocation(allowMockLocation),
  ]);
  return initialState;
}

/**
 * Removes the association of the device with the user by resetting the 'name' of the device and the 'driver_handle' to empty strings
 */
async function resetHyperTrack(initialState: InitialState) {
  const { allowMockLocation } = initialState;

  await Promise.all([
    HyperTrack.setName(""),
    HyperTrack.setMetadata({ driver_handle: "" }),
    HyperTrack.setAllowMockLocation(allowMockLocation),
  ]);
}

/**
 * Initializes the HyperTrack SDK based on the user's feature flag and userId
 */
export function useHyperTrack(userId: string | undefined): void {
  const allowMockLocation = useCbhFlag(CbhFeatureFlag.HYPERTRACK_ALLOW_MOCK_LOCATION, {
    defaultValue: false,
  });
  const hyperTrackSdkStateRef = useRef(HyperTrackSdkState.NOT_INITIALIZED);
  const initialStateRef = useRef<InitialState>({ allowMockLocation: false });
  const isValidUser = isDefined(userId) && userId !== "";

  const updateHyperTrackLifecycle = useCallback(async () => {
    if (isValidUser && hyperTrackSdkStateRef.current === HyperTrackSdkState.NOT_INITIALIZED) {
      logEvent(APP_V2_APP_EVENTS.HYPER_TRACK_TRIAL_ENABLED);
      try {
        hyperTrackSdkStateRef.current = HyperTrackSdkState.INITIALIZING;
        const initializationParams = {
          userId,
          allowMockLocation,
        };
        initialStateRef.current = await initializeHyperTrack(initializationParams);
        logEvent(APP_V2_APP_EVENTS.HYPER_TRACK_INIT_SUCCESS, {
          initialState: initialStateRef.current,
          initializationParams,
        });
        hyperTrackSdkStateRef.current = HyperTrackSdkState.INITIALIZED;
      } catch (error) {
        logError(APP_V2_APP_EVENTS.HYPER_TRACK_INIT_FAILURE, {
          error,
          metadata: {
            userId,
          },
        });
        hyperTrackSdkStateRef.current = HyperTrackSdkState.INITIALIZATION_FAILED;
      }
    } else if (!isValidUser && hyperTrackSdkStateRef.current === HyperTrackSdkState.INITIALIZED) {
      try {
        // 'turn off' hypertrack if the user is not a hypertrack pilot user or if the userId is not defined (i.e., logged out).
        hyperTrackSdkStateRef.current = HyperTrackSdkState.RESETTING;
        await resetHyperTrack(initialStateRef.current);
        logEvent(APP_V2_APP_EVENTS.HYPER_TRACK_RESET_SUCCESS);
        hyperTrackSdkStateRef.current = HyperTrackSdkState.NOT_INITIALIZED;
      } catch (error) {
        logError(APP_V2_APP_EVENTS.HYPER_TRACK_RESET_FAILURE, {
          error,
        });
        hyperTrackSdkStateRef.current = HyperTrackSdkState.RESET_FAILED;
      }
    }
  }, [allowMockLocation, isValidUser, userId]);

  useEffect(() => {
    if (isCapacitorPlatform()) {
      void updateHyperTrackLifecycle();
    }
  }, [updateHyperTrackLifecycle]);
}
